/** El pie de una respuesta: procedencia del turno, coste real y el feedback que alimenta la
 *  learning base. El coste se muestra siempre, incluido el cero del modo local. */
import { useState } from "react";
import { api } from "../lib/api";
import { ms, usd } from "../lib/formato";
import { t } from "../lib/i18n";
import type { Idioma, Turno } from "../lib/tipos";
import { ChipProcedencia } from "./ChipProcedencia";

export function PieRespuesta({ turno, idioma, sesionId }: { turno: Turno; idioma: Idioma; sesionId: string | null }) {
  const txt = t(idioma);
  const [abierto, setAbierto] = useState(false);
  const [autor, setAutor] = useState("");
  const [comentario, setComentario] = useState("");
  const [estado, setEstado] = useState<"" | "enviando" | "listo" | "error">("");
  const [aviso, setAviso] = useState("");
  const meta = turno.meta;
  if (!meta) return null;

  const votar = async (voto: "arriba" | "abajo", validado = false) => {
    if (!turno.turno_id) return;
    setEstado("enviando");
    try {
      const r = await api.feedback({ turno_id: turno.turno_id, voto, validado_por_especialista: validado, autor, comentario });
      setEstado("listo");
      setAviso(r.aviso || "");
      if (r.nota_creada) setAbierto(false);
    } catch {
      setEstado("error");
    }
  };

  return (
    <footer className="pie-respuesta">
      <div className="pie-meta">
        {meta.origen && <ChipProcedencia origen={meta.origen} idioma={idioma} />}
        <span>{meta.corte || meta.tokens ? `${txt.corte}: ${meta.corte}` : ""}</span>
        <span>{meta.backend}{meta.modelo ? ` · ${meta.modelo}` : ""}</span>
        <span>{txt.coste} {usd(meta.costo_usd)}</span>
        <span>{meta.rondas} {txt.rondas}</span>
        <span>{ms(meta.latencia_ms)}</span>
        {meta.validacion.reintentos > 0 && <span className="chip aviso">{txt.reintento}</span>}
        {meta.validacion.degradado && <span className="chip aviso">{txt.degradado}</span>}
        {meta.fallback_usado && <span className="chip aviso">fallback</span>}
      </div>

      <div className="pie-acciones">
        {sesionId && (
          <>
            <a className="boton-plano" href={api.urlExport(sesionId, "md")}>{txt.exportarMd}</a>
            <a className="boton-plano" href={api.urlExport(sesionId, "html")}>{txt.exportarHtml}</a>
          </>
        )}
        <button className="boton-plano" onClick={() => votar("arriba")} disabled={estado === "enviando"}>👍 {txt.util}</button>
        <button className="boton-plano" onClick={() => votar("abajo")} disabled={estado === "enviando"}>👎 {txt.noUtil}</button>
        <button className="boton-plano" onClick={() => setAbierto((v) => !v)} aria-expanded={abierto}>★ {txt.validar}</button>
        {estado === "listo" && <span className="ok">{txt.guardado}</span>}
        {estado === "error" && <span className="mal">{txt.error}</span>}
      </div>

      {abierto && (
        <form className="validar" onSubmit={(e) => { e.preventDefault(); void votar("arriba", true); }}>
          <p className="ayuda">{txt.validarAyuda}</p>
          <input value={autor} onChange={(e) => setAutor(e.target.value)} placeholder={txt.autor} maxLength={120} required />
          <textarea value={comentario} onChange={(e) => setComentario(e.target.value)} placeholder={txt.comentario} maxLength={2000} rows={3} />
          <button type="submit" className="boton" disabled={estado === "enviando"}>{txt.guardar}</button>
          {aviso && <p className="mal">{aviso}</p>}
        </form>
      )}
    </footer>
  );
}
